import {
  detectIdCollisions,
  mergeContent,
  type ContentPack,
  type ResolvedContent,
} from './pack.js';

export interface ContentValidationIssue {
  readonly path: string;
  readonly message: string;
  readonly suggestion?: string;
}

const SUGGESTION_DISTANCE_THRESHOLD = 3;

const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const prev: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let last = i - 1;
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const temp = prev[j]!;
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      prev[j] = Math.min(prev[j]! + 1, prev[j - 1]! + 1, last + cost);
      last = temp;
    }
  }
  return prev[b.length]!;
};

const suggestSimilar = (target: string, candidates: Iterable<string>): string | undefined => {
  let best: { id: string; distance: number } | undefined;
  for (const candidate of candidates) {
    const distance = levenshtein(target, candidate);
    if (distance <= SUGGESTION_DISTANCE_THRESHOLD && (best === undefined || distance < best.distance)) {
      best = { id: candidate, distance };
    }
  }
  return best?.id;
};

const buildIssue = (
  path: string,
  message: string,
  missingId: string,
  candidates: Iterable<string>,
): ContentValidationIssue => {
  const suggestion = suggestSimilar(missingId, candidates);
  return suggestion !== undefined
    ? { path, message, suggestion: `Did you mean "${suggestion}"?` }
    : { path, message };
};

// Every id-reference field in the content schemas, mapped to the
// category whose ids it must resolve into. Kept in sync with the
// reference keys the pack-integrity audit checks. A field carrying one
// of these names anywhere in any entry (top-level or nested in effects /
// riders / grants) must point at a defined entity in the merged set.
const REF_TARGETS: ReadonlyArray<{
  readonly key: string;
  readonly target: keyof ResolvedContent;
}> = [
  { key: 'originFeatId', target: 'feats' },
  { key: 'parentClassId', target: 'classes' },
  { key: 'spellId', target: 'spells' },
  { key: 'enchantmentDefinitionId', target: 'items' },
  { key: 'conditionId', target: 'conditions' },
  { key: 'conditionOnFail', target: 'conditions' },
  { key: 'conditionOnSuccess', target: 'conditions' },
  { key: 'allyConditionId', target: 'conditions' },
  { key: 'applyConditionId', target: 'conditions' },
  { key: 'bearerConditionId', target: 'conditions' },
];
const TARGET_OF = new Map(REF_TARGETS.map((r) => [r.key, r.target] as const));

// Visits every string-valued (key, value) pair anywhere under `node`.
const walkStringFields = (
  node: unknown,
  visit: (key: string, value: string) => void,
): void => {
  if (Array.isArray(node)) {
    for (const v of node) walkStringFields(v, visit);
    return;
  }
  if (node !== null && typeof node === 'object') {
    for (const [k, v] of Object.entries(node)) {
      if (typeof v === 'string') visit(k, v);
      else walkStringFields(v, visit);
    }
  }
};

const CATEGORIES: ReadonlyArray<keyof ResolvedContent> = [
  'species', 'backgrounds', 'classes', 'subclasses', 'feats', 'spells', 'items', 'monsters', 'conditions',
];

// Asserts that every id-reference field across the merged content
// resolves to a defined entity. Catches a renamed / deleted / mistyped
// id that an entry still references (the grant or rider would silently
// never resolve). Each distinct dangling (key, value) is reported once,
// with a Levenshtein "did you mean" suggestion when a near id exists.
export const validateCrossReferences = (
  content: ResolvedContent,
): ReadonlyArray<ContentValidationIssue> => {
  const issues: ContentValidationIssue[] = [];
  const reported = new Set<string>();

  for (const category of CATEGORIES) {
    for (const [entryId, entry] of content[category]) {
      walkStringFields(entry, (key, value) => {
        const target = TARGET_OF.get(key);
        if (target === undefined) return;
        if (content[target].has(value)) return;
        const dedupe = `${key} ${value}`;
        if (reported.has(dedupe)) return;
        reported.add(dedupe);
        issues.push(
          buildIssue(
            `${category}.${entryId}.${key}`,
            `${key} reference "${value}" not found`,
            value,
            content[target].keys(),
          ),
        );
      });
    }
  }

  return issues;
};

// Author-time validator for one or more content packs (the report-all
// companion to `resolveContent`, which fails fast). Surfaces id
// collisions (within-pack duplicates and undeclared cross-pack clobbers)
// and dangling cross-references across the merged set, without throwing,
// so a pack author sees every problem at once. Run it over the union the
// engine will load, e.g. `validatePacks([srdPack, myHomebrewPack])`.
export const validatePacks = (
  packs: ReadonlyArray<ContentPack>,
): ReadonlyArray<ContentValidationIssue> => [
  ...detectIdCollisions(packs),
  ...validateCrossReferences(mergeContent(packs)),
];
